import React, { } from "react";
import { colors, glyphs, useMediaQuery} from "../../../Styles";
import { ColumnFilter, ColumnFiltersState } from "@tanstack/react-table";
import NewButton, { NewButtonList } from "../../BaseComponents/Buttons/newButton";
import { ButtonTypes, ListStyle_Types } from "../../../Definitions";

type filterDisplayListProps = {
  filters: ColumnFiltersState
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  disabled?: boolean
} 

export default function FilterDisplayList({ filters, setColumnFilters, disabled = false }: filterDisplayListProps) {
  const flex10 = useMediaQuery(`(max-width: 710px), (min-width: 1001px) and (max-width: 1030px)`);
  const flex30 = useMediaQuery(`(min-width: 711px) and (max-width: 800px), (min-width: 1031px) and (max-width: 1100px)`);
  const flex50 = useMediaQuery(`(min-width: 801px) and (max-width: 880px), (min-width: 1101px) and (max-width: 1200px)`);
  const flex150 = useMediaQuery(`(min-width: 881px) and (max-width: 950px), (min-width: 1201px) and (max-width: 1300px)`);
  const flex250 = useMediaQuery(`(min-width: 951px) and (max-width: 1000px), (min-width: 1301px) and (max-width: 1450px)`);
  const flex350 = useMediaQuery(`(min-width: 1451px)`);

  const containerWidth =
    (flex10 && 10) ||
    (flex30 && 30) ||  
    (flex50 && 50) ||
    (flex150 && 150) || 
    (flex250 && 250) ||
    (flex350 && 350) ||
    250;

  // Remove filter from list
  const onClearFilter = (filterId: string, valueToRemove: { id?: number; label: string }) => {
    setColumnFilters((prev) => {
      return prev
        .map((filter) => {
          if (filter.id !== filterId) return filter;
  
          if (Array.isArray(filter.value)) {
            const newValues = filter.value.filter((v) => v.id !== valueToRemove.id);
            if (newValues.length === 0) {
              return null;
            }
            return { ...filter, value: newValues };
          }
  
          return null;
        })
        .filter((f): f is ColumnFilter => f !== null);
    });
  };

// 1. Flad alle filtre ud
const flattenedFilters = filters.flatMap(filter => {
  if (Array.isArray(filter.value)) {
    return filter.value.map(v => ({ id: filter.id, value: v }));
  }
  return [{ id: filter.id, value: filter.value }];
});

// 2. Beregn hvilke der kan vises inden for containerWidth
const calculateVisibleFiltersAcrossAll = (
  filters: { id: string; value: { label: string } }[],
  maxWidth: number,
  buttonPadding = 26
) => {
  let totalWidth = 0;
  const first: typeof filters = [];
  const rest: typeof filters = [];

  for (let f of filters) {
    const approxWidth = f.value.label.length * 7 + buttonPadding;
    if (totalWidth + approxWidth <= maxWidth) {
      first.push(f);
      totalWidth += approxWidth;
    } else {
      rest.push(f);
    }
  }

  return { first, rest };
};

const { first: visibleFilters, rest: hiddenFilters } = calculateVisibleFiltersAcrossAll(flattenedFilters, containerWidth);


  return ( <> { flattenedFilters.length > 0 ?
    <div style = {{
      display: "flex",
      margin: flex30 ? "10px 0px" : "10px",
      gap: "5px",
      scrollbarColor: colors.greyPrimary + colors.transparent,
      scrollbarWidth: "thin",
      borderRadius: "10px", 
      alignItems: "center",
      opacity: disabled ? "0.5" : "1",
      width: containerWidth
    }}>
    
    {visibleFilters.map((filter, i) => (
    <NewButton
      key={`${filter.id}-${i}`}
      content={filter.value.label}
      buttonType={ButtonTypes.tagButton}
      endEnhancer={glyphs.Close({ size: "10px" })}
      onClick={() => onClearFilter(filter.id, filter.value)}
      disabled={disabled}
      overrides={{
        baseStyles: { backgroundColor: colors.greySecondary },
        hoverStyles: { backgroundColor: colors.greyPrimary },
        disabledStyles: { opacity: "0.5" }
      }}
    />
  ))}
  
  {hiddenFilters.length > 0 && !flex10 && (
    <NewButtonList
      contents={hiddenFilters.map(f => f.value.label)}
      onClick={(btnIndex) => onClearFilter(hiddenFilters[btnIndex].id, hiddenFilters[btnIndex].value)}
      buttonType={ButtonTypes.tagButton}
      endEnhancers={hiddenFilters.map(v => glyphs.Close({ size: "10px" }))}
      listOverrides={{ listStyle: ListStyle_Types.vertical }}
      overrides={{
        baseStyles: { backgroundColor: colors.greyDarkSecondary, padding: "5px 6px" },
        hoverStyles: { backgroundColor: colors.blackPrimary },
        disabledStyles: { opacity: "0.5" }
      }}
      popOverrides={{
        hasPop: true,
        buttonType: ButtonTypes.tagButton,
        popoverStyle: { top: "27px", backgroundColor: colors.greyDarkPrimary, padding: "10px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d"},
        openContent: `+ ${hiddenFilters.length} filters`,
        closeContent: `+ ${hiddenFilters.length} filters`,
        openEnhancer: <></>,
        closeEnhancer: <></>,
        openCloseStyle: { baseStyles: { backgroundColor: colors.transparent }, hoverStyles: { backgroundColor: colors.transparent, opacity: "0.3" } }
      }}
    />
  )}
    </div >
  : <></>} </> );
}